// src/components/quotes/quoteRecord.js
//
// THE ONE FACTORY FOR A QUOTE RECORD.
//
// ⚠ Until this existed there were THREE independent object literals — the
// Opportunity path, the backlog path and Stage D — and no shared shape at all.
// CLAUDE.md already records where that leads, for proposals: "Two entry points
// with two independent object literals is exactly how one ends up missing
// whatever field the other gained." That is why makeProposalRecord exists.
// Quotes had three, and the Q number is precisely the kind of field that would
// have reached two of them and been forgotten in the third — quietly minting
// unnumbered quotes forever, from the path nobody was looking at.
//
// Kept React-free so it can be unit-tested and so onboardingQuotes.js, which
// is deliberately import-free, can use it.
import { seedQuoteTerms } from "./quoteTerms";
import { emptyFulfilment } from "./quoteBoard";

/* ⚠ THE API CLIENT IS IMPORTED LAZILY. apiClient constructs the MSAL
   PublicClientApplication at module load and jsdom has no WebCrypto, so a
   static import here would kill every test suite that touches quotes before
   its first assertion. Learned the expensive way while wiring the P number
   into templateDefaults, which broke ten suites at once. */
async function defaultReserve(name) {
  const { reserveNumber } = await import("../../api/sequenceApi");
  return reserveNumber(name);
}

/**
 * A quote record, with its Q number reserved.
 *
 * ⚠ ASYNC, because the number comes from an atomic server-side counter — it
 * cannot be derived in the browser, where two quotes created in quick
 * succession would take the same one. Forgetting to `await` yields a Promise
 * where a record is expected, which breaks loudly rather than leaving a quote
 * quietly unnumbered.
 *
 * A reservation failure does NOT lose the user's work: the quote is created
 * with a null number and can be given one later, exactly as a proposal is.
 */
export async function makeQuoteRecord({
  seed,                     // Stage D mints several at once and supplies its own
  now = Date.now(),
  title = "New quote",
  description = "",
  companyId = "",
  companyName = "",
  contactName = "",
  contactEmail = "",
  sections = [],
  backlogId = null,
  opportunityId = null,
  proposalId = null,
  extra = {},
  reserve = defaultReserve,
  /* ⚠ THE TERMS ARE SNAPSHOTTED HERE, at creation, from whatever Settings
     says today. They are never re-read from Settings afterwards — a quote is
     the record of what was offered, and acceptance is about to become
     financially operative. quoteTerms.js carries the full reasoning.

     Omitting it is safe rather than silent: ensureMigrated backfills any
     quote that arrives without terms, so a creation path that forgets this
     argument still ends up with the default rather than with none. This
     argument is what pins the wording to the moment of creation. */
  termsDefault,
} = {}) {
  let quoteNumber = null;
  try {
    quoteNumber = (await reserve("quote")).number;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Could not reserve a quote number; the quote is created without one.", e);
  }

  return {
    /* ⚠ The id carries the seed when one is given. Stage D creates several
       quotes in the same millisecond, so `qt-${Date.now()}` alone collides —
       every quote after the first would share an id, and React keys and every
       id lookup in the builder would silently address the wrong record. */
    id: `qt-${seed != null ? seed : now}`,
    quoteNumber,
    createdAt: now,
    updatedAt: now,
    backlogId,
    ...(opportunityId ? { opportunityId } : {}),
    ...(proposalId ? { proposalId } : {}),
    companyId,
    companyName,
    contactName,
    contactEmail,
    title,
    description,
    sections,
    commonLineItems: [],
    status: "draft",
    /* The second axis: how far the paperwork has got AFTER a win. Timestamps,
       not booleans, and independent of each other — quoteBoard.js has the
       ruling. ensureMigrated backfills any stored quote that predates this. */
    fulfilment: emptyFulfilment(),
    validUntil: "",
    sentAt: null,
    decidedAt: null,
    notes: "",
    terms: seedQuoteTerms(termsDefault),
    pdfUrl: null,
    autotaskQuoteId: null,
    ...extra,
  };
}

/** Q3500. The one place the prefix is written. */
export function formatQuoteNumber(n) {
  return n == null || n === "" ? "" : `Q${n}`;
}

/**
 * The title for a quote Stage D generates from a proposal.
 *
 * ⚠ THIS CARRIES THE SOURCE PROPOSAL'S **P** NUMBER, NOT THE QUOTE'S OWN Q.
 * That is not an inconsistency, it is the decision: a pipeline quote is a
 * document about a proposal, so its title says which one. The Q number is DATA
 * shown beside the title, never part of it — and a standalone quote's title is
 * never rewritten at all.
 *
 * ⚠ WITH NO PROPOSAL NUMBER IT RETURNS THE DESCRIPTION ALONE, which is
 * EXACTLY what Stage D produced before this existed — a block quote was titled
 * for its block ("Office Setup") and the fees quote "Setup Fees". An
 * un-numbered proposal is real (a record created while the counter was
 * unreachable, until the backfill catches it), and it must come out unchanged.
 *
 * Note the backend's dry-run preview rendered this as "{client} — {block}" and
 * so had ALWAYS disagreed with the quote actually created. Both now say the
 * same thing, in both branches.
 */
export function pipelineQuoteTitle(proposal, desc, clientName) {
  const n = Number(proposal?.proposalNumber);
  return Number.isFinite(n) && n > 0 ? `P${n} - ${desc} - ${clientName}` : desc;
}

/** Match a quote against a typed search term — "3500", "Q3500", "q3500". */
export function matchesQuoteNumberSearch(q, term) {
  const s = String(term || "").trim().toLowerCase().replace(/^q/, "");
  if (!s) return true;
  if (q?.quoteNumber == null || q.quoteNumber === "") return false;
  return String(q.quoteNumber).includes(s);
}
